"use client";
import React, { FormEvent, useState } from "react";

import Image from "next/image";
import Link from "next/link";

import { CgMenuRound } from "react-icons/cg";

import MobileNav from "./MobileNav";
import ThemeButton from "@/app/(Global)/ThemeButton";

import { RootState } from "@/app/GlobalRedux/Store";
import { useDispatch, useSelector } from "react-redux";
import { UserData } from "@/app/GlobalRedux/features/auth/authInterfaces";
import { emailListFormData } from "@/app/GlobalRedux/features/emailSubscribe/emailSubscribeInterface";
import {
  RESET,
  SubscribeToMailList,
} from "@/app/GlobalRedux/features/emailSubscribe/emailSubscribeSlice";

import { regex } from "@/app/utils/forms-utils/regex";
import styling from "./BlogHeader.module.scss";
import { IoCloseCircleOutline } from "react-icons/io5";

interface BlogHeaderProps {
  theme: string;
  user: UserData;
  isLoggedIn: boolean;
  isLoadingUser: boolean;
}

const BlogHeader: React.FC<BlogHeaderProps> = ({
  theme,
  user,
  isLoggedIn,
  isLoadingUser,
}) => {
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const fullName = user?.name;
  const userFirstName = fullName?.split(" ")[0];

  const dispatch = useDispatch();

  const [emailError, setEmailError] = useState("");
  const [email, setEmail] = useState("");
  const { isLoadingEmailSub } = useSelector(
    (state: RootState) => state.emailSubscribe
  );

  const validateEmail = (email: string) => {
    return regex.test(email);
  };

  const handleSubscribeToEmailList = async (e: FormEvent) => {
    e.preventDefault();

    // Validate the email
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");

    // create the data
    const data: emailListFormData = {
      emailAddress: email,
    };

    // console.log(data);
    await dispatch(SubscribeToMailList(data) as any);
    await dispatch(RESET());
    setEmail("");
  };

  return (
    <>
      <header
        className={`${styling.header} ${
          theme === "dark" ? styling.darkMode : ""
        }`}
      >
        <div className={styling.sideLeft}>
          <div>
            <Link href="/">
              <span>
                <Image
                  src="https://ik.imagekit.io/vinnie/BLOG/Vinnie-tec_62aUaX5Hu.png?updatedAt=1684410998966"
                  alt="Vinnietec-Logo"
                  width={450}
                  height={450}
                />
              </span>
              <span>
                <strong>Blog</strong>
              </span>
            </Link>
          </div>
          <nav>
            <ul>
              <li>
                <Link
                  href="https://vinnietec.netlify.app/"
                  style={{
                    textDecoration: "none",
                    boxShadow: "0 -1px 1px 1px rgb(4, 2, 69)",
                    padding: "5px 10px",
                    fontSize: ".8rem",
                  }}
                >
                  VINNIETEC
                </Link>
              </li>
              <li>
                <Link href="/siteinfo/aboutus">About Us</Link>
              </li>
              {isLoggedIn && (
                <>
                  <Link
                    href="/user-profile"
                    style={{
                      color: "rgb(4, 2, 69)",
                      boxShadow: "0 -1px 1px 1px rgb(4, 2, 69)",
                      padding: "5px 10px",
                      borderRadius: "3px",
                      display: "inline-block",
                      fontSize: ".8rem",
                    }}
                  >
                    Your Profile
                  </Link>
                  {!isLoggedIn && <Link href="/auth/signin">Sign In</Link>}
                  {user?.userIsVerified === true &&
                  (user?.role === "Super Admin" ||
                    user?.role === "Admin" ||
                    user?.role === "Editor") ? (
                    <Link
                      href={user?.role === "Editor" ? "/admin/posts" : "/admin"}
                      style={{
                        color: "rgb(4, 2, 69)",
                        boxShadow: "0 -1px 1px 1px rb(4, 2, 69)",
                        padding: "5px 10px",
                        borderRadius: "3px",
                        display: "inline-block",
                        fontSize: "0.8rem",
                      }}
                    >
                      {user?.role === "Editor" ? "Editor Panel" : "Admin Panel"}
                    </Link>
                  ) : (
                    ""
                  )}
                  {user?.userIsVerified === true && user?.role === "Author" ? (
                    <Link
                      href="/author"
                      style={{
                        color: "rgb(4, 2, 69)",
                        boxShadow: "0 -1px 1px 1px rb(4, 2, 69)",
                        padding: "5px 10px",
                        borderRadius: "3px",
                        display: "inline-block",
                        fontSize: "0.8rem",
                      }}
                    >
                      Author Panel
                    </Link>
                  ) : (
                    ""
                  )}
                </>
              )}
            </ul>
          </nav>
        </div>
        <div className={styling.sideRight}>
          {!isLoadingUser && isLoggedIn ? (
            <div>
              <span>
                <Image
                  src={user?.photo}
                  alt="User-Logo"
                  width={450}
                  height={450}
                />
              </span>
              <h5>Hi, {userFirstName}</h5>
            </div>
          ) : (
            ""
          )}
          <span>
            <ThemeButton size={25} />
          </span>
          <span
            onClick={() => {
              setIsNavExpanded(!isNavExpanded);
            }}
          >
            {isNavExpanded ? (
              <IoCloseCircleOutline size={30} />
            ) : (
              <CgMenuRound size={30} />
            )}
          </span>
        </div>
        {isNavExpanded && (
          <MobileNav
            setIsNavExpanded={setIsNavExpanded}
            isNavExpanded={isNavExpanded}
            user={user}
            isLoggedIn={isLoggedIn}
          />
        )}
      </header>
      <section
        className={`${styling.section} ${
          theme === "dark" ? styling.darkMode : ""
        }`}
      >
        <div>
          <h1>Level Up Your Tech Journey with Our Bi-Weekly Newsletter!</h1>
          <p>
            Get inspired and level up your tech journey with our bi-weekly
            newsletter. Receive motivating content, valuable tips, and insights
            to help both newbies and intermediate developers grow their skills.
            Don&#39;t miss out on the opportunity to stay updated and motivated!
          </p>
        </div>
        <form onSubmit={handleSubscribeToEmailList}>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {emailError && <p className={styling.error}>{emailError}</p>}
          <button type="submit" disabled={isLoadingEmailSub}>
            {isLoadingEmailSub ? "Subscribing..." : "Subscribe"}
          </button>
        </form>
      </section>
    </>
  );
};

export default BlogHeader;
